import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Collapse, Pagination } from 'react-bootstrap';
import recallService from '../../services/recalls.service';
import Loading from '../common/Loading';
import ProcessQueueFilters from '../process/ProcessQueueFilters';
import {
    RECALL_STATUS_BADGES,
    formatStatusLabel,
    truncateText,
} from '../process/processQueueUtils';
const SEARCH_FIELDS = [
    { value: 'title', label: 'Title' },
    { value: 'clinicName', label: 'Clinic Name' },
    { value: 'notes', label: 'Notes' },
];

const SORT_FIELDS = [
    { value: 'createdAt', label: 'Created At' },
    { value: 'modifiedAt', label: 'Modified At' },
    { value: 'title', label: 'Title' },
    { value: 'status', label: 'Status' },
    { value: 'clinicName', label: 'Clinic Name' },
];

const STATUS_OPTIONS = ['PENDING', 'IN_PROCESS', 'COMPLETED', 'FAILED'];

function RecallTable({ searchPanelOpen = false }) {
    const navigate = useNavigate();
    const [currentItems, setCurrentItems] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [searchField, setSearchField] = useState('title');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');

    useEffect(() => {
        let isActive = true;

        setIsLoading(true);
        setError(null);

        recallService
            .getRecallRequests(currentPage, itemsPerPage, {
                sortBy,
                sortOrder,
                searchField,
                searchTerm: appliedSearch,
                statusFilter,
            })
            .then((res) => {
                if (!isActive) {
                    return;
                }
                setCurrentItems(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                if ((res.data.content || []).length === 0 && (appliedSearch || statusFilter)) {
                    setError('No recall requests match your filters.');
                }
            })
            .catch((err) => {
                if (!isActive) {
                    return;
                }
                console.error(err);
                setError('Failed to load recall requests. Please try again.');
                setCurrentItems([]);
            })
            .finally(() => {
                if (isActive) {
                    setIsLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [currentPage, itemsPerPage, appliedSearch, statusFilter, sortBy, sortOrder, searchField]);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        setAppliedSearch(searchTerm);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearch('');
        setStatusFilter('');
        setCurrentPage(0);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleView = (id) => {
        navigate(`edit/${id}`);
    };

    const StatusBadge = ({ status }) => {
        const badgeClass = RECALL_STATUS_BADGES[status] || 'text-bg-secondary';
        return <span className={`badge ${badgeClass}`}>{formatStatusLabel(status)}</span>;
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage + 1);
        let endPage = Math.min(totalPages - 1, currentPage + 3);

        if (currentPage < 2) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={currentPage === totalPages - 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = currentItems.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const DetailField = ({ label, children, mono }) => (
        <div className="col-md-4 col-lg-3 mb-2">
            <div className="text-muted small">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    return (
        <>
            <Collapse in={searchPanelOpen}>
                <div>
                    <ProcessQueueFilters
                        searchTerm={searchTerm}
                        onSearchTermChange={setSearchTerm}
                        searchField={searchField}
                        onSearchFieldChange={setSearchField}
                        searchFields={SEARCH_FIELDS}
                        statusFilter={statusFilter}
                        onStatusFilterChange={(value) => {
                            setStatusFilter(value);
                            setCurrentPage(0);
                        }}
                        statusOptions={STATUS_OPTIONS}
                        sortBy={sortBy}
                        onSortByChange={(value) => {
                            setSortBy(value);
                            setCurrentPage(0);
                        }}
                        sortFields={SORT_FIELDS}
                        sortOrder={sortOrder}
                        onSortOrderChange={(value) => {
                            setSortOrder(value);
                            setCurrentPage(0);
                        }}
                        onSubmit={handleSearchSubmit}
                        onClear={handleClearFilters}
                        canClear={Boolean(appliedSearch || statusFilter || searchTerm)}
                        showSort
                    />
                </div>
            </Collapse>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <span className="text-muted small">
                    {totalElements} request{totalElements !== 1 ? 's' : ''} total
                </span>
                {paginationControls}
            </div>

            {isLoading ? (
                <Loading />
            ) : error && currentItems.length === 0 ? (
                <Alert variant="warning">{error}</Alert>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                        <thead className="table-light">
                            <tr>
                                <th>
                                    <i className="bi bi-building"></i> Clinic
                                </th>
                                <th>
                                    <i className="bi bi-file-text"></i> Title
                                </th>
                                <th>
                                    <i className="bi bi-calendar"></i> Created
                                </th>
                                <th>
                                    <i className="bi bi-activity"></i> Status
                                </th>
                                <th style={{ width: '120px' }} className="text-center">
                                    <i className="bi bi-gear"></i>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center text-muted py-4">
                                        No recall requests found.
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((item) => {
                                    const isPending = item.status === 'PENDING';
                                    const rowClassName = [
                                        isPending ? 'fw-bold' : '',
                                        expandedRowId === item.id ? 'table-active' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ');

                                    return (
                                    <React.Fragment key={item.id}>
                                        <tr className={rowClassName || undefined}>
                                            <td>{item.clinicName || '—'}</td>
                                            <td>
                                                <small title={item.title}>{truncateText(item.title, 48)}</small>
                                            </td>
                                            <td>
                                                <small>{item.createdAt || '—'}</small>
                                            </td>
                                            <td>
                                                <StatusBadge status={item.status} />
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleView(item.id)}
                                                        title="View recall request"
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </Button>
                                                    <Button
                                                        variant={
                                                            expandedRowId === item.id
                                                                ? 'outline-secondary'
                                                                : 'outline-primary'
                                                        }
                                                        size="sm"
                                                        onClick={() => toggleRow(item.id)}
                                                        title={expandedRowId === item.id ? 'Collapse' : 'Expand'}
                                                        aria-expanded={expandedRowId === item.id}
                                                    >
                                                        <i
                                                            className={`bi bi-chevron-${
                                                                expandedRowId === item.id ? 'up' : 'down'
                                                            }`}
                                                        ></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr className="p-0 border-top-0">
                                            <td colSpan={5} className="p-0 border-top-0">
                                                <Collapse in={expandedRowId === item.id}>
                                                    <div className="p-3 bg-light border-top">
                                                        <div className="row">
                                                            <DetailField label="Request ID" mono>
                                                                {item.id}
                                                            </DetailField>
                                                            <DetailField label="Clinic ID" mono>
                                                                {item.clinicId ? (
                                                                    <Link
                                                                        to={`/clinics/${item.clinicId}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        {item.clinicId}
                                                                    </Link>
                                                                ) : (
                                                                    '—'
                                                                )}
                                                            </DetailField>
                                                            <DetailField label="Clinic Name">{item.clinicName}</DetailField>
                                                            <DetailField label="Title">{item.title}</DetailField>
                                                            <DetailField label="Status">
                                                                <StatusBadge status={item.status} />
                                                            </DetailField>
                                                            <DetailField label="Created At">{item.createdAt}</DetailField>
                                                            <DetailField label="Modified At">{item.modifiedAt}</DetailField>
                                                            <DetailField label="File Path" mono>
                                                                {item.filePath}
                                                            </DetailField>
                                                            <DetailField label="Processed File Path" mono>
                                                                {item.processedFilePath}
                                                            </DetailField>
                                                            <div className="col-12 mb-2">
                                                                <div className="text-muted small">Notes</div>
                                                                <pre className="small mb-0 bg-white border rounded p-2 text-break">
                                                                    {item.notes || '—'}
                                                                </pre>
                                                            </div>
                                                            <div className="col-12 mb-2">
                                                                <div className="text-muted small">Admin Notes</div>
                                                                <pre className="small mb-0 bg-white border rounded p-2 text-break">
                                                                    {item.adminNotes || '—'}
                                                                </pre>
                                                            </div>
                                                        </div>
                                                        <div className="mt-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleView(item.id)}
                                                            >
                                                                <i className="bi bi-pencil-square me-1"></i>
                                                                Manage request
                                                            </button>
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small">Show</span>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: '80px' }}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {[10, 20, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                        <span className="small">entries</span>
                    </div>
                    <span className="text-muted small">
                        Showing {startItem} to {endItem} of {totalElements}
                    </span>
                </div>
                {paginationControls}
            </div>
        </>
    );
}

export default RecallTable;
